import faunaData from '@/data/fauna.json';
import {
  FAUNA_CLASSES,
  type FaunaAttachment,
  type FaunaClass,
  type FaunaDefinition,
  type FaunaLicense,
} from './FaunaDefinition';
import { assertValidFaunaDefinition } from './FaunaValidation';

/**
 * Loads, validates, and indexes the shipped fauna catalog.
 *
 * Follows `FloraCatalog` rather than `PropCatalog`, because a fauna record is a description of an
 * animal and only incidentally a description of a mesh: invalid records throw on load rather than
 * being dropped, so a catalog that would ship nineteen of the twenty creatures it claims fails the
 * build instead of failing quietly in someone's design.
 *
 * The query surface is what a palette needs — class sections, tag facets, a name search that also
 * matches scientific names — kept here rather than in the UI so a placement tool, the capture
 * manifest, and a browser all answer "what can I place" the same way.
 */

interface FaunaCatalogFile {
  catalogVersion: string;
  schemaVersion: number;
  fauna: unknown[];
}

export class FaunaCatalog {
  private readonly byId = new Map<string, FaunaDefinition>();
  readonly version: string;

  constructor(file: FaunaCatalogFile) {
    this.version = file.catalogVersion;
    for (const record of file.fauna) {
      assertValidFaunaDefinition(record);
      if (this.byId.has(record.id)) throw new Error(`Duplicate fauna id in catalog: ${record.id}`);
      this.byId.set(record.id, record);
    }
  }

  get size(): number {
    return this.byId.size;
  }

  /** All definitions in load order. */
  list(): FaunaDefinition[] {
    return [...this.byId.values()];
  }

  /** Look up a definition by id, or throw when unknown. */
  get(id: string): FaunaDefinition {
    const definition = this.byId.get(id);
    if (!definition) throw new Error(`Unknown fauna id: ${id}`);
    return definition;
  }

  /** Look up a definition by id, returning undefined when unknown. */
  find(id: string): FaunaDefinition | undefined {
    return this.byId.get(id);
  }

  has(id: string): boolean {
    return this.byId.has(id);
  }

  byClass(faunaClass: FaunaClass): FaunaDefinition[] {
    return this.list().filter((definition) => definition.class === faunaClass);
  }

  /** Classes actually present, in canonical order — what a palette should show as sections. */
  classes(): FaunaClass[] {
    return FAUNA_CLASSES.filter((faunaClass) => this.byClass(faunaClass).length > 0);
  }

  /** Everything placeable in a given way — the list a placement tool filters by. */
  byAttachment(attachment: FaunaAttachment): FaunaDefinition[] {
    return this.list().filter((definition) => definition.attachment.includes(attachment));
  }

  /** Every tag in use, sorted, for a facet list. */
  tags(): string[] {
    const tags = new Set<string>();
    for (const definition of this.list()) for (const tag of definition.tags) tags.add(tag);
    return [...tags].sort();
  }

  /**
   * Free-text search over display name, common names, scientific name, and tags.
   *
   * Scientific names are searched because they are the half of the catalog a designer is most likely
   * to be handed by a client or an ecologist, and a palette that only matches "cottontail" cannot
   * find *Sylvilagus floridanus* written on a planting schedule.
   */
  search(query: string): FaunaDefinition[] {
    const needle = query.trim().toLowerCase();
    if (needle.length === 0) return this.list();
    return this.list().filter((definition) => {
      const haystack = [
        definition.name,
        definition.scientificName ?? '',
        ...(definition.commonNames ?? []),
        ...definition.tags,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }

  /** Records that have at least one mesh — the rest render as massing. */
  withAssets(): FaunaDefinition[] {
    return this.list().filter(
      (definition) => definition.asset !== undefined && Object.keys(definition.asset.files).length > 0,
    );
  }

  /**
   * Poses declared but not yet modelled, as `id/poseId` pairs.
   *
   * The catalog is authored ahead of its assets on purpose, so "what is still missing" is a standing
   * question rather than an error state. This is the answer, and it is what an importer's coverage
   * report should read.
   */
  unmodelledPoses(): { id: string; poseId: string }[] {
    const missing: { id: string; poseId: string }[] = [];
    for (const definition of this.list()) {
      for (const pose of definition.poses) {
        if (!definition.asset?.files[pose.id]) missing.push({ id: definition.id, poseId: pose.id });
      }
    }
    return missing;
  }

  /**
   * Attribution lines, deduplicated by author and licence — the same grouping `PropCatalog` uses,
   * because a credits panel that repeats one line twenty times is one nobody reads.
   */
  attributions(): { license: FaunaLicense; fauna: FaunaDefinition[] }[] {
    const groups = new Map<string, { license: FaunaLicense; fauna: FaunaDefinition[] }>();
    for (const definition of this.list()) {
      const license = definition.asset?.license;
      if (!license) continue;
      const key = `${license.author} ${license.spdx}`;
      const group = groups.get(key) ?? { license, fauna: [] };
      group.fauna.push(definition);
      groups.set(key, group);
    }
    return [...groups.values()];
  }
}

/** The default catalog loaded from the shipped `fauna.json`. */
export function loadDefaultFaunaCatalog(): FaunaCatalog {
  return new FaunaCatalog(faunaData);
}
